"use strict";

import CustomScrollbar from "@/components/CustomScrollbar.vue";
import FloatingVue from "floating-vue";
import { createPinia } from "pinia";
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { registerSettlementsStore } from "./store/settlementsStoreRegistry";
import useSettlementsStore from "./store/useSettlementsStore";

import * as Sentry from "@sentry/vue";
import "floating-vue/dist/style.css";
import "./assets/styles/global.scss";
import "./assets/styles/main.scss";
import "./assets/styles/vue-transitions.scss";
import { ENV, IS_PROD } from "./config/constants";
import Communicator from "./libs/AppCommunicatior";
import GameError, { GameErrorType } from "./libs/GameError";
import { initializeFlutterWindowMethods } from "./utils/flutterWindowMethods";
import { appVersion, sentryDsn, sentryEnvironment } from "./utils/url";

window.version = __APP_BUILD_VERSION__;
Communicator.getInstance().sendGameVersion(
	IS_PROD
		? __APP_BUILD_VERSION__
		: `${__APP_BUILD_VERSION__} ${ENV.toUpperCase()}`,
);
initializeFlutterWindowMethods();

const el = document.getElementById("app");
if (!el) throw new Error("Element with id 'app' not found");

el.setAttribute("data-build-version", `${__APP_BUILD_VERSION__}`);
el.setAttribute("data-build-timestamp", `${__APP_BUILD_TIMESTAMP__}`);

const app = createApp(App);
const pinia = createPinia();

if (IS_PROD && sentryDsn !== "") {
	Sentry.init({
		app,
		dsn: sentryDsn,
		release: appVersion,
		environment: sentryEnvironment,

		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: true,

		// Set sample rate for error events
		sampleRate: 1.0,

		// Add better error tracking integrations
		integrations: [Sentry.browserTracingIntegration()],

		tracePropagationTargets: ["https://api.legacyofkingsgame.com"],

		// Group errors by message
		// https://docs.sentry.io/platforms/javascript/enriching-events/fingerprinting/#group-errors-more-aggressively
		beforeSend: (
			event: Sentry.ErrorEvent,
			hint: Sentry.EventHint,
		): Sentry.ErrorEvent | null => {
			const exception = hint.originalException;

			if (exception instanceof GameError) {
				if (
					[
						GameErrorType.BE_TIMEOUT_ERROR,
						GameErrorType.NETWORK_DISCONNECT_DURING_REQUEST,
					].includes(exception.type)
				)
					return null; // Ignore timeout and disconnect errors

				event.fingerprint = [exception.type, exception.message];
				return event;
			}

			if (exception instanceof Error) {
				event.fingerprint = [exception.message];
				return event;
			}

			return event;
		},
	});
}

app.component(CustomScrollbar.name as string, CustomScrollbar);

app.use(pinia);
registerSettlementsStore(useSettlementsStore);
app.use(router).use(FloatingVue);

app.mount(el);

declare module "vue" {
	export interface GlobalComponents {
		CustomScrollbar: typeof CustomScrollbar;
	}
}
